// src/pages/Login.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { signInWithEmailAndPassword } from 'firebase/auth';
import {
  Box,
  Button,
  Input,
  Heading,
  VStack,
  useToast,
  Text,
  Image,
  Center,
  Link,
} from '@chakra-ui/react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const hasAdminAccessFromToken = (t) => {
    const isGlobalAdmin = t?.claims?.platformOwner === true;
    const isOrgAdmin =
      t?.claims?.orgId &&
      (t?.claims?.role === 'admin' || t?.claims?.role === 'superadmin');
    return !!(isGlobalAdmin || isOrgAdmin);
  };

  const waitForAdminAccess = useCallback(
    async (user, timeoutMs = 8000, intervalMs = 300) => {
      const start = Date.now();
      try {
        while (Date.now() - start < timeoutMs) {
          await user.getIdToken(true);
          const t = await user.getIdTokenResult();
          if (hasAdminAccessFromToken(t)) return true;
          await new Promise((r) => setTimeout(r, intervalMs));
        }
      } catch (e) {
        console.error('Error while waiting for admin access claim:', e);
      }
      return false;
    },
    []
  );

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const hasAccess = await waitForAdminAccess(user);
        if (cancelled) return;

        if (hasAccess) {
          navigate('/', { replace: true });
        } else {
          await auth.signOut();
          toast({
            title: 'Access denied',
            description: (
              <span>
                This panel is for admins only. Please use the{' '}
                <Link href="https://mobile.fixihub.pro" color="blue.500" isExternal>
                  Sales App
                </Link>{' '}
                instead.
              </span>
            ),
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, toast, waitForAdminAccess]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);

      const hasAccess = await waitForAdminAccess(cred.user);

      if (hasAccess) {
        navigate('/', { replace: true });
      } else {
        await auth.signOut();
        toast({
          title: 'Access denied',
          description: (
            <span>
              This panel is for admins only. Please use the{' '}
              <Link href="https://mobile.fixihub.pro" color="blue.500" isExternal>
                Sales App
              </Link>{' '}
              instead.
            </span>
          ),
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
      let description = 'Unknown error';
      if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password') {
        description = 'Incorrect email or password.';
      } else if (error?.code === 'auth/user-not-found') {
        description = 'No account found with that email.';
      } else if (error?.message) {
        description = error.message;
      }

      toast({
        title: 'Login failed',
        description,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center minH="100vh" bg="gray.50">
      <Box maxW="md" w="full" bg="white" p={8} borderWidth={1} borderRadius="lg" shadow="md">
        <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />

        <Heading as="h1" size="lg" textAlign="center" mb={6}>
          Admin Login
        </Heading>

        <form onSubmit={onSubmit}>
          <VStack spacing={4}>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
              size="lg"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
              size="lg"
            />
            
            {/* Reverting to a less prominent link style and placement */}
            <Text fontSize="sm" color="gray.600" alignSelf="flex-end" mt={-2} mb={4}>
              <RouterLink
                to="/forgot-password"
                style={{ color: 'var(--chakra-colors-blue-500)', fontWeight: 'normal' }}
              >
                Forgot password?
              </RouterLink>
            </Text>

            <Button type="submit" colorScheme="blue" isLoading={loading} width="full" size="lg">
              Log in
            </Button>

            <Text fontSize="sm" color="gray.600" pt={4}>
              New here?{' '}
              <RouterLink
                to="/signup"
                style={{ color: 'var(--chakra-colors-blue-500)', fontWeight: 'bold' }}
              >
                SignUp
              </RouterLink>
            </Text>
          </VStack>
        </form>
      </Box>
    </Center>
  );
}